import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { companyInfo } from '../data/mock';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { MessageCircle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const Checkout = () => {
  const { t } = useLanguage();
  const { cart, getCartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    city: ''
  });

  // Redirect to cart if cart is empty
  useEffect(() => {
    if (cart.length === 0) {
      navigate('/cart');
    }
  }, [cart.length, navigate]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name || !formData.phone || !formData.city) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    // Build WhatsApp message
    const message = `Hola, quiero reservar estos productos desde la página de NAGOC GROUP:

${cart.map(item => `• ${item.name} x${item.quantity} - RD$${(item.price * item.quantity).toLocaleString()}`).join('\n')}

Total estimado: RD$${getCartTotal().toLocaleString()}

Nombre: ${formData.name}
Teléfono: ${formData.phone}
Ciudad: ${formData.city}

Quiero confirmar disponibilidad.`;

    const whatsappUrl = `https://wa.me/${companyInfo.social.whatsapp.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
    
    // Open WhatsApp
    window.open(whatsappUrl, '_blank');
    
    // Clear cart and redirect
    toast.success('Redirigiendo a WhatsApp...');
    setTimeout(() => {
      clearCart();
      navigate('/');
    }, 2000);
  };

  // Don't render if cart is empty (will redirect via useEffect)
  if (cart.length === 0) {
    return null;
  }

  return (
    <div className="min-h-screen py-12">
      <div className="container mx-auto px-4 max-w-5xl">
        <Button
          variant="ghost"
          className="mb-8"
          onClick={() => navigate('/cart')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver al carrito
        </Button>

        <h1 className="text-4xl font-bold mb-8">{t('completeOrder')}</h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Customer Info Form */}
          <Card>
            <CardHeader>
              <CardTitle>{t('yourInfo')}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{t('fullName')}</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Juan Pérez"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">{t('phone')}</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+1 809 123 4567"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="city">{t('city')}</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="Santiago de los Caballeros"
                    required
                  />
                </div>

                <Separator className="my-6" />

                <div className="space-y-3 text-sm text-muted-foreground">
                  <p>✓ {t('deliveryAvailable')}</p>
                  <p>✓ {t('professionalInstallation')} ({t('installationCost')})</p>
                  <p>✓ {t('financingSubject')}</p>
                </div>

                <Button type="submit" size="lg" className="w-full mt-6">
                  <MessageCircle className="h-5 w-5 mr-2" />
                  {t('sendWhatsApp')}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Order Summary */}
          <Card>
            <CardHeader>
              <CardTitle>{t('orderSummary')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                {cart.map(item => {
                  // Use monthly_payment from product data
                  const monthlyPayment = item.monthly_payment || 0;
                  return (
                    <div key={item.id} className="space-y-1">
                      <div className="flex justify-between">
                        <div className="flex-1">
                          <p className="font-medium leading-tight">{item.name}</p>
                          <p className="text-sm text-muted-foreground">
                            Cantidad: {item.quantity}
                          </p>
                          {monthlyPayment > 0 && (
                            <p className="text-xs text-muted-foreground">
                              {t('from')} RD${monthlyPayment.toLocaleString()}{t('perMonth')}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="font-semibold">
                            RD${(item.price * item.quantity).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <Separator />
                    </div>
                  );
                })}
              </div>

              <div className="space-y-2 pt-4">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('subtotal')}</span>
                  <span className="font-medium">RD${getCartTotal().toLocaleString()}</span>
                </div>
                <Separator />
                <div className="flex justify-between text-lg font-bold">
                  <span>{t('total')}</span>
                  <span>RD${getCartTotal().toLocaleString()}</span>
                </div>
              </div>

              <Card className="bg-muted/50 border-0 mt-6">
                <CardContent className="p-4">
                  <p className="text-sm font-medium mb-2">{t('takeItToday')}</p>
                  <p className="text-xs text-muted-foreground">
                    {t('payComfortably')}. {t('financingSubject')}.
                  </p>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
